import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { MinotaureInvestisseurFilterInput, RechercheSauvegardeFragmentFragment } from '@lib/models/generated/graphql';
import { Subject } from 'rxjs';
import { BuilderCode, LineOperator } from './filtre-builders/filtre-builder.model';
import { FiltreLine } from './filtre-lines/filtre-line.model';
import { FiltreLineService } from './filtre-lines/filtre-line.service';
import { FiltrerInvestisseursPanelService } from './filtrer-investisseurs-panel.service';
import { RechercheSauvegardeService } from './recherche-sauvegarde.service';

export type InvestisseurFilters = {
  filter?: MinotaureInvestisseurFilterInput;
  lines?: FiltreLine[];
};
@Injectable({
  providedIn: 'root',
})
export class FiltreManagerService {
  private readonly filtreLineService = inject(FiltreLineService);
  private readonly rechercheService = inject(RechercheSauvegardeService);
  private readonly panelService = inject(FiltrerInvestisseursPanelService);

  private readonly filtreInputChangeSubj$ = new Subject<void>();
  public readonly filtreInputChange$ = this.filtreInputChangeSubj$.asObservable();
  public readonly selectedFiltreInput = signal<InvestisseurFilters | undefined>(undefined);

  public readonly selectedRechercheSauvegarde = signal<RechercheSauvegardeFragmentFragment | undefined>(undefined);

  constructor() {
    this.filtreLineService.cleanAllLines();
    this.panelService.viewerClosed$.subscribe(() => {
      this.selectedRechercheSauvegarde.set(undefined);
    });
    this.updateFiltreInput();
  }

  private buildAllFiltres(): InvestisseurFilters {
    return this.buildLineFilters([...this.filtreLineService.allLines()]);
  }

  updateFiltreInput() {
    this.selectedFiltreInput.set(this.buildAllFiltres());
    this.filtreInputChangeSubj$.next();
  }

  onClearAllAndUpdate() {
    this.filtreLineService.cleanAllLines();
    this.updateFiltreInput();
  }

  resetLines() {
    this.filtreLineService.cleanAllLines();
  }

  removeFilter(button: boolean, order: number | null | undefined) {
    if (order === undefined || order === null) {
      return;
    }
    if (button) {
      this.filtreLineService.clearButtonLine(order);
      this.updateFiltreInput();
      return;
    }
    this.filtreLineService.removeCustomLine(order);
    this.updateFiltreInput();
  }

  runFiltreWithSettings(settings: { builderCode: BuilderCode; operator: LineOperator; value: any }[]) {
    this.filtreLineService.setLinesFromFiltreSettings(settings);
    this.updateFiltreInput();
  }
  parseFiltreSettingJsonValues(
    data: {
      builderCode: string;
      operator: string;
      value: string;
    }[]
  ): { builderCode: BuilderCode; operator: LineOperator; value: any }[] {
    const filtres = data;
    const parsedFiltres = filtres
      .map(f => this.parseFiltreSettingJsonValue(f))
      .filter((f): f is { builderCode: BuilderCode; operator: LineOperator; value: any } => f !== null);
    return parsedFiltres;
  }
  private parseFiltreSettingJsonValue(data: {
    builderCode: string;
    operator: string;
    value: string;
  }): { builderCode: BuilderCode; operator: LineOperator; value: any } | null {
    try {
      const filtreBuilderCode = data.builderCode as BuilderCode;
      const operator = data.operator as LineOperator;
      const value = JSON.parse(data.value);
      return { builderCode: filtreBuilderCode, operator, value };
    } catch (error) {
      if (isDevMode()) {
        console.error('Error parsing filtre setting JSON value:', error);
      }
      return null;
    }
  }

  static parseFilterToString(filter: object | null | undefined): string {
    if (filter === null || filter === undefined) {
      return '';
    }
    const processValue = (value: any): string => {
      if (value === null || value === undefined) {
        return 'null';
      }
      const valueType = typeof value;
      if (valueType === 'string') {
        // Les chaînes de caractères sont entourées de guillemets
        return `"${value}"`;
      }
      if (valueType === 'number' || valueType === 'boolean') {
        // Les nombres et les booléens sont tels quels
        return value.toString();
      }
      if (Array.isArray(value)) {
        // Traitement récursif pour les tableaux
        return `[${value.map(item => processValue(item)).join(',')}]`;
      }
      if (valueType === 'object') {
        // Traitement récursif pour les objets
        const properties = Object.keys(value)
          .map(key => `${key}:${processValue(value[key])}`)
          .join(',');
        return `{${properties}}`;
      }
      // Fallback pour d'autres types, bien que non attendu avec les types GraphQL
      return value.toString();
    };

    return processValue(filter);
  }

  private buildLineFilters(lines: FiltreLine[]): InvestisseurFilters {
    const completedLines = lines.filter(
      line =>
        line.value !== null &&
        line.value !== undefined &&
        (Array.isArray(line.value) ? line.value.length > 0 : true) &&
        !!line.builder &&
        !!line.operator
    );

    const filters = completedLines.map(line => line.builder?.buildFilter(line.operator!, line.value)).filter(f => !!f);

    const showAriane = sessionStorage.getItem('showAriane') === 'true';
    if (!showAriane) {
      const minotaureOnlyFilter: MinotaureInvestisseurFilterInput = { isTransferedToMinotaure: { eq: true } };
      filters.push(minotaureOnlyFilter);
    }

    const filter = filters.length > 1 ? { and: filters } : filters.length > 0 ? filters[0] : undefined;

    return { filter, lines: completedLines };
  }

  buildInvestisseurFilterFromSettings(
    settings: {
      builderCode: BuilderCode;
      operator: LineOperator;
      value: any;
    }[]
  ): MinotaureInvestisseurFilterInput | undefined {
    const savedLines: FiltreLine[] = settings
      .map(f => this.filtreLineService.mapFiltreDataToLine(f))
      .filter(line => !!line) as FiltreLine[];
    return this.buildLineFilters(savedLines).filter;
  }

  openRecherche(id: number) {
    const selectedSearch = this.rechercheService.allRecherchesSubject.getValue().find(r => r.id === id);
    if (selectedSearch) {
      this.resetLines();
      const filters = selectedSearch.filtres;
      this.filtreLineService.setLinesFromFiltreSettings(this.parseFiltreSettingJsonValues(filters));
      this.panelService.openFilterPanel();
    }
  }

  runRecherche(id: number) {
    const selectedSearch = this.rechercheService.allRecherchesSubject.getValue().find(r => r.id === id);
    if (selectedSearch) {
      const filters = selectedSearch.filtres;
      const parsedFilters = this.parseFiltreSettingJsonValues(filters);
      this.runFiltreWithSettings(parsedFilters);
    }
  }

  openAndEditRecherche(id: number) {
    const selectedSearch = this.rechercheService.allRecherchesSubject.getValue().find(r => r.id === id);
    if (selectedSearch) {
      this.resetLines();
      this.selectedRechercheSauvegarde.set(selectedSearch);
      const filters = selectedSearch.filtres;
      this.filtreLineService.setLinesFromFiltreSettings(this.parseFiltreSettingJsonValues(filters));
      this.panelService.openFilterPanel();
    }
  }

  async createRecherche(nom: string) {
    const lines = this.selectedFiltreInput()?.lines?.filter(line => line.value !== null && !!line.builder && !!line.operator) ?? [];
    await this.rechercheService.createRecherche(nom, lines);
  }

  updateFiltreInputAndRecherche(rechercheId: number, nomRecherche: string) {
    if (!rechercheId || !nomRecherche) {
      return;
    }
    this.updateFiltreInput();
    const lines = this.selectedFiltreInput()?.lines?.filter(line => line.value !== null && !!line.builder && !!line.operator) ?? [];
    this.rechercheService.updateRecherche(rechercheId, nomRecherche, lines);
  }
  async deleteRecherche(id: number) {
    await this.rechercheService.deleteRecherche(id);
  }
}
